/**
 * R-77 — per-section provider relevance whitelist.
 *
 * Maps audit-framework provider names to orchestrator ids via normalizeProviderId.
 * Human-review: section allowlists are seeded from report section semantics —
 * SME review recommended before tightening block severity in production.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { normalizeProviderId } from './providerCoverageRegistry';

export interface ProviderRelevanceRule {
  allowed: readonly string[];
  preferred: readonly string[];
}

export type ProviderRelevanceBySection = Record<string, ProviderRelevanceRule>;

/** Orchestrator ids internally; alias audit names at verify time via normalizeProviderId. */
export const PROVIDER_RELEVANCE: Record<ReportTypeId, ProviderRelevanceBySection> = {
  feasibility: {
    supplyAvailability: {
      allowed: ['wri-aqueduct', 'open-meteo', 'usfws-env', 'site-research'],
      preferred: ['wri-aqueduct'],
    },
    groundwater: {
      allowed: ['wri-aqueduct', 'usfws-env', 'open-meteo', 'fema-flood'],
      preferred: ['wri-aqueduct'],
    },
    waterRights: {
      allowed: ['wri-aqueduct', 'site-research', 'usfws-env'],
      preferred: ['wri-aqueduct', 'site-research'],
    },
    droughtResilience: {
      allowed: ['wri-aqueduct', 'open-meteo', 'fema-flood'],
      preferred: ['wri-aqueduct', 'open-meteo'],
    },
    // STAGE 5.1 — the grid-screening half absorbed from power-availability.
    // Copied VERBATIM from the 'power-availability' rows below: the sections
    // read the same providers whichever report carries them, and without these
    // rows the provider-relevance audit simply would not evaluate the six
    // sections on a feasibility report (a missing key is "no opinion", not a
    // pass). Commit 2 removes the power-availability copies, not these.
    //
    // CORRECTED 2026-09-05: these rows were originally inserted into the
    // 'test-fit' block by mistake — the patch anchored on
    // `constructabilityAssessment`, which test-fit and feasibility both
    // carry. test-fit does not have these sections at all, so the rows were
    // inert there AND absent here, which is exactly the 'no opinion' hole
    // this entry exists to close.
    gridCapacity: {
      allowed: ['hifld-grid', 'eia-electricity', 'nrc-reactors', 'peeringdb-ix'],
      preferred: ['hifld-grid', 'eia-electricity'],
    },
    interconnectionQueue: {
      allowed: ['eia-electricity', 'hifld-grid', 'site-research'],
      preferred: ['eia-electricity'],
    },
    rateStructures: {
      allowed: ['eia-electricity', 'site-research'],
      preferred: ['eia-electricity'],
    },
    curtailmentRisk: {
      allowed: ['eia-electricity', 'nrel-solar', 'nrel-wind', 'open-meteo'],
      preferred: ['eia-electricity'],
    },
    renewableIntegration: {
      allowed: ['nrel-solar', 'nrel-wind', 'eia-electricity', 'open-meteo'],
      preferred: ['nrel-solar', 'nrel-wind'],
    },
    gridRedundancy: {
      allowed: ['hifld-grid', 'nrc-reactors', 'eia-electricity'],
      preferred: ['hifld-grid', 'nrc-reactors'],
    },
    gridStudy: {
      allowed: ['hifld-grid', 'eia-electricity', 'peeringdb-ix', 'nrc-reactors', 'site-research'],
      preferred: ['hifld-grid', 'eia-electricity'],
    },
    climateSevereWeather: {
      allowed: ['open-meteo', 'usgs-seismic', 'fema-flood', 'site-research'],
      preferred: ['open-meteo', 'usgs-seismic'],
    },
    transportStudy: {
      allowed: ['site-research', 'hifld-grid', 'open-meteo'],
      preferred: ['site-research'],
    },
    connectivityStudy: {
      allowed: ['peeringdb-ix', 'hifld-grid', 'eia-electricity', 'site-research'],
      preferred: ['peeringdb-ix'],
    },
    sitingNotes: {
      allowed: ['site-research', 'fema-flood', 'usfws-env', 'regrid-parcels', 'open-meteo'],
      preferred: ['site-research', 'fema-flood'],
    },
    btmOverview: {
      allowed: ['nrel-solar', 'nrel-wind', 'site-research', 'eia-electricity'],
      preferred: ['nrel-solar', 'nrel-wind'],
    },
    projectIntentOverview: {
      allowed: ['site-research', 'hifld-grid', 'open-meteo'],
      preferred: ['site-research'],
    },
  },
  environmental: {
    nepaScreening: {
      allowed: ['site-research', 'usfws-env', 'fema-flood', 'regulatory-db'],
      preferred: ['site-research', 'usfws-env'],
    },
    ecologicalAssessment: {
      allowed: ['usfws-env', 'fema-flood', 'site-research', 'open-meteo'],
      preferred: ['usfws-env'],
    },
    waterResources: {
      allowed: ['wri-aqueduct', 'usfws-env', 'open-meteo', 'fema-flood'],
      preferred: ['wri-aqueduct', 'usfws-env'],
    },
    airQualityEmissions: {
      allowed: ['airnow-aqi', 'open-meteo', 'site-research'],
      preferred: ['airnow-aqi'],
    },
    floodNaturalHazards: {
      allowed: ['fema-flood', 'open-meteo', 'usgs-seismic', 'site-research'],
      preferred: ['fema-flood'],
    },
    landUseZoning: {
      allowed: ['site-research', 'regrid-parcels', 'usfws-env'],
      preferred: ['site-research', 'regrid-parcels'],
    },
    culturalHistorical: {
      allowed: ['site-research', 'usfws-env', 'regulatory-db'],
      preferred: ['site-research'],
    },
    permittingPathway: {
      allowed: ['site-research', 'usfws-env', 'regulatory-db', 'fema-flood'],
      preferred: ['site-research'],
    },
  },
  'test-fit': {
    parcelOverview: {
      allowed: ['regrid-parcels', 'site-research', 'fema-flood'],
      preferred: ['regrid-parcels'],
    },
    topography: {
      allowed: ['regrid-parcels', 'site-research', 'usgs-seismic', 'open-meteo'],
      preferred: ['regrid-parcels', 'site-research'],
    },
    buildingPlacement: {
      allowed: ['regrid-parcels', 'site-research', 'fema-flood'],
      preferred: ['regrid-parcels'],
    },
    setbackCompliance: {
      allowed: ['regrid-parcels', 'site-research', 'fema-flood'],
      preferred: ['regrid-parcels'],
    },
    stormwater: {
      allowed: ['fema-flood', 'wri-aqueduct', 'site-research', 'regrid-parcels'],
      preferred: ['fema-flood'],
    },
    maxCapacity: {
      allowed: ['regrid-parcels', 'hifld-grid', 'nrel-solar', 'nrel-wind', 'site-research'],
      preferred: ['regrid-parcels', 'hifld-grid'],
    },
    constructabilityAssessment: {
      allowed: ['site-research', 'regrid-parcels', 'usgs-seismic', 'fema-flood'],
      preferred: ['site-research'],
    },
  },
  'power-availability': {
    gridCapacity: {
      allowed: ['hifld-grid', 'eia-electricity', 'nrc-reactors', 'peeringdb-ix'],
      preferred: ['hifld-grid', 'eia-electricity'],
    },
    interconnectionQueue: {
      allowed: ['eia-electricity', 'hifld-grid', 'site-research'],
      preferred: ['eia-electricity'],
    },
    rateStructures: {
      allowed: ['eia-electricity', 'site-research'],
      preferred: ['eia-electricity'],
    },
    curtailmentRisk: {
      allowed: ['eia-electricity', 'nrel-solar', 'nrel-wind', 'open-meteo'],
      preferred: ['eia-electricity'],
    },
    renewableIntegration: {
      allowed: ['nrel-solar', 'nrel-wind', 'eia-electricity', 'open-meteo'],
      preferred: ['nrel-solar', 'nrel-wind'],
    },
    gridRedundancy: {
      allowed: ['hifld-grid', 'nrc-reactors', 'eia-electricity'],
      preferred: ['hifld-grid', 'nrc-reactors'],
    },
    powerStrategyContext: {
      allowed: ['hifld-grid', 'eia-electricity', 'nrel-solar', 'nrel-wind', 'site-research'],
      preferred: ['hifld-grid', 'eia-electricity'],
    },
  },
  'water-resource': {
    supplyAvailability: {
      allowed: ['wri-aqueduct', 'open-meteo', 'usfws-env', 'site-research'],
      preferred: ['wri-aqueduct'],
    },
    groundwater: {
      allowed: ['wri-aqueduct', 'usfws-env', 'open-meteo', 'fema-flood'],
      preferred: ['wri-aqueduct'],
    },
    waterRights: {
      allowed: ['wri-aqueduct', 'site-research', 'usfws-env'],
      preferred: ['wri-aqueduct', 'site-research'],
    },
    droughtResilience: {
      allowed: ['wri-aqueduct', 'open-meteo', 'fema-flood'],
      preferred: ['wri-aqueduct', 'open-meteo'],
    },
    coolingByTech: {
      allowed: ['wri-aqueduct', 'open-meteo', 'site-research', 'nrel-solar'],
      preferred: ['wri-aqueduct'],
    },
    wastewaterStormwater: {
      allowed: ['fema-flood', 'wri-aqueduct', 'usfws-env', 'site-research'],
      preferred: ['fema-flood', 'wri-aqueduct'],
    },
    conservation: {
      allowed: ['wri-aqueduct', 'open-meteo', 'site-research'],
      preferred: ['wri-aqueduct'],
    },
  },
  interconnection: {
    fiberRings: {
      allowed: ['peeringdb-ix', 'site-research', 'hifld-grid'],
      preferred: ['peeringdb-ix'],
    },
    latencyModel: {
      allowed: ['peeringdb-ix', 'site-research'],
      preferred: ['peeringdb-ix'],
    },
    carrierDiversity: {
      allowed: ['peeringdb-ix', 'site-research'],
      preferred: ['peeringdb-ix'],
    },
    ixProximity: {
      allowed: ['peeringdb-ix', 'site-research'],
      preferred: ['peeringdb-ix'],
    },
    submarineCable: {
      allowed: ['peeringdb-ix', 'site-research'],
      preferred: ['peeringdb-ix'],
    },
    networkResilience: {
      allowed: ['peeringdb-ix', 'site-research', 'hifld-grid'],
      preferred: ['peeringdb-ix'],
    },
    bandwidthCapacity: {
      allowed: ['peeringdb-ix', 'site-research', 'eia-electricity'],
      preferred: ['peeringdb-ix'],
    },
  },
  'supply-chain-risk': {
    procurementOverview: {
      allowed: ['site-research', 'hifld-grid', 'eia-electricity'],
      preferred: ['site-research'],
    },
    criticalPathProcurement: {
      allowed: ['site-research', 'hifld-grid'],
      preferred: ['site-research'],
    },
    supplyChainRisks: {
      allowed: ['site-research', 'hifld-grid', 'eia-electricity'],
      preferred: ['site-research'],
    },
    supplierRiskRanking: {
      allowed: ['site-research'],
      preferred: ['site-research'],
    },
    quoteComparisons: {
      allowed: ['site-research'],
      preferred: ['site-research'],
    },
    supplyChainRiskMatrix: {
      allowed: ['site-research', 'hifld-grid'],
      preferred: ['site-research'],
    },
  },
  'public-sentiment': {
    stakeholderMap: {
      allowed: ['public-sentiment', 'site-research'],
      preferred: ['public-sentiment'],
    },
    perBtmSentiment: {
      allowed: ['public-sentiment', 'site-research'],
      preferred: ['public-sentiment'],
    },
    themeDeepDives: {
      allowed: ['public-sentiment', 'site-research'],
      preferred: ['public-sentiment'],
    },
    trendAnalysis: {
      allowed: ['public-sentiment', 'site-research'],
      preferred: ['public-sentiment'],
    },
    comparableSiteBenchmark: {
      allowed: ['public-sentiment', 'site-research'],
      preferred: ['public-sentiment'],
    },
    mitigationPlaybook: {
      allowed: ['public-sentiment', 'site-research'],
      preferred: ['public-sentiment'],
    },
  },
  'custom-brief': {},
  /*
   * FIN-1 (wf w7nuyquwd), revised for audit 3.8 — Financial Viability Report.
   *
   * `{}` disabled R-77 for this type entirely (`resolveSectionRule` returns
   * null the moment a type's map is empty), so a citation to ANY provider was
   * relevant everywhere. The old rationale — "body is engine-computed, not
   * provider-grounded" — is true of the FIGURES and false of the SECTIONS: the
   * deterministic body carries no `[Ref N]` at all, and this verifier only
   * looks at sections that DO carry one. Every ref it can see therefore came
   * from the export narrative, which is exactly the text that needs policing.
   *
   * The vocabulary below is the market/cost evidence a financial section can
   * legitimately lean on — EIA for power prices and fuel, NREL for technology
   * cost and resource, `regulatory-db` for the tax/credit regime, HIFLD where
   * the pathway comparison prices interconnection — plus `site-research`,
   * which is the only provider that carries project-specific commercial
   * material. Sections with no plausible external source (`decisionBand`,
   * `cashflowStream`, `monteCarloBands`, `financialActionPlan`) are left
   * UNLISTED rather than given a permissive allowlist: an unlisted section is
   * skipped, which is honest, where a wide allowlist would look like a rule
   * and enforce nothing.
   */
  financial: {
    financialSummary: {
      allowed: ['site-research', 'eia-electricity', 'nrel-solar', 'nrel-wind'],
      preferred: ['eia-electricity', 'site-research'],
    },
    capexOpexBreakdown: {
      allowed: ['site-research', 'eia-electricity', 'nrel-solar', 'nrel-wind'],
      preferred: ['site-research'],
    },
    riskAdjustedWacc: {
      allowed: ['site-research', 'eia-electricity'],
      preferred: ['site-research'],
    },
    sensitivity: {
      allowed: ['site-research', 'eia-electricity', 'nrel-solar', 'nrel-wind'],
      preferred: ['site-research'],
    },
    taxAndCommercial: {
      allowed: ['site-research', 'regulatory-db', 'eia-electricity'],
      preferred: ['site-research', 'regulatory-db'],
    },
    pathway_comparison: {
      allowed: ['site-research', 'eia-electricity', 'nrel-solar', 'nrel-wind', 'hifld-grid'],
      preferred: ['site-research'],
    },
  },
  'executive-summary': {
    crossReportSynthesis: {
      allowed: ['site-research', 'hifld-grid', 'eia-electricity', 'open-meteo', 'usfws-env'],
      preferred: ['site-research'],
    },
    criticalRisks: {
      allowed: ['site-research', 'hifld-grid', 'eia-electricity', 'open-meteo'],
      preferred: ['site-research'],
    },
  },
};

export function getProviderRelevanceRules(reportType: ReportTypeId): ProviderRelevanceBySection {
  return PROVIDER_RELEVANCE[reportType] ?? {};
}

export function normalizeAllowedList(providers: readonly string[]): string[] {
  return providers.map((p) => normalizeProviderId(p));
}
